import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import { Card, CardContent, CardMedia, Divider, Grid, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useEffect, useMemo, useState } from "react";
import { showErrorNotification, showSuccessNotification, showWarningNotification } from "../../../components/admin/common/NotiService";
import { OZCustomToggleButton } from "../../../components/admin/common/OZCustomToggleButton";
import LoadingScreen from "../../../components/admin/common/OZLoadingScreen/OZLoadingScreen";
import SimpleSwitch from "../../../components/admin/common/OZSwitch/SimpleSwitch";
import { enableIdeskIntegration, forceRevokeIntegration, getAvailableIntegration, getEnabledUserIntegrations, removeIdeskIntegration, removeIntegrations, removeSsoLoginORCloudStorage, revokeIntegration, toggleContactManagerIntegrationData } from "../../../services/api-service";
import { liveReportService } from "../../../services/liveReportService";
import { NewAdminPages, newAuthPages, newAuthSubAdminAccess, writeAccess } from "../../../services/page-service";
import balto from "./balto.svg";
import BaltoIntegration from "./BaltoIntegration";
import CloudStorage from "./CloudStorage";
import cloudStorageImage from "./cloudstorage.svg";
import hubspot from "./hubspot.svg";
import { IdeskIntegration } from "./IdeskIntegration";
import Integration from "./Integration";
import "./Integration.css";
import netsapiens from "./netsapiens.png";
import NetsapiensIntegration from "./NetsapiensIntegration";
import ozonetel from "./ozonetel.svg";
import SSOLogin from "./SSOLogin";
import ssoImage from "./ssologin.svg";
import zoho from "./Zoho.png";
import { ReactComponent as ResetIcon } from '../../../static/icons/reset.svg';
import { ContactManagerIntegration } from "./ContactManagerIntegration";

const SSO_INTEGRATION_TYPES = ["SAML", "LDAP", "OAUTH2"];

const parseSsoAuthConfig = (authConfig) => {
  if (!authConfig) {
    return {};
  }

  if (typeof authConfig === "object") {
    return authConfig;
  }

  try {
    return JSON.parse(authConfig);
  } catch {
    console.error("Unable to parse SSO authentication configuration");
    return {};
  }
};

const useStyles = makeStyles({
  root: {
    width: 313,
    Height: 142,
  },
  zohoImage: {
    height: 30,
    width: 90,
    cursor: "pointer",
  },
  ozonetelImage: {
    height: 37,
    width: 170,
    cursor: "pointer"
  },
  hubspotImage: {
    height: 37,
    width: 140,
    cursor: "pointer"
  },
  ssoImage: {
    height: 50,
    width: 50,
    cursor: "pointer",
  },
  cloudStorageImage: {
    height: 50,
    width: 92,
    cursor: "pointer",
  },
  zohoImageWithoutCursor: {
    height: 30,
    width: 90,
  },
  ozonetelImageWithoutCursor: {
    height: 37,
    width: 170,
  },
  hubspotImageWithoutCursor: {
    height: 37,
    width: 140,
  },
  ssoImageWithoutCursor: {
    height: 50,
    width: 50,
  },
  cloudStorageImageWithoutCursor: {
    height: 50,
    width: 92,
  },
  cardPara: {
    color: "#212121",
    paddingLeft: "8px",
    height: "14px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: 400
  },
  cardParaWithoutCursor: {
    color: "#212121",
    paddingLeft: "8px",
    height: "14px",
    fontSize: "12px",
    fontWeight: 400
  },
});

export default function IntegrationsHome() {
  const classes = useStyles();
  const [enabledIntegrations, setEnableIntegrations] = useState([{}]);
  const [integrationList, setIntegrationList] = useState([]);
  const [loading, setLoading] = useState();
  const [ideskIntegration, setIdeskIntegration] = useState(false);
  const [ideskData, setIdeskData] = useState([]);
  const [contactManagerIntegration, setContactManagerIntegration] = useState(false);
  const [contactManagerData, setContactManagerData] = useState([]);
  const [zohoIntegration, setZohoIntegration] = useState(false);
  const [hubspotIntegration, setHubspotIntegration] = useState(false);
  const [netsapiensIntegration, setNetsapiensIntegration] = useState(false);
  const [netsapiensData, setNetsapiensData] = useState(null);
  const [baltoIntegration, setBaltoIntegration] = useState(false);
  const [baltoData, setBaltoData] = useState(null);
  const [crmTab, setCrmTab] = useState(true);
  const [operationalTab, setOperationalTab] = useState(false);
  const [enabledOperationalIntegrations, setEnableOperationalIntegrations] = useState({
    SAML: false,
    LDAP: false,
    OAUTH2: false,
    cloudStorage: false
  });
  const [ssoLogin, setSsoLogin] = useState(false);
  const [selectedSsoType, setSelectedSsoType] = useState("SAML");
  const [cloudStorage, setCloudStorage] = useState(false);
  const [ssoLoginData, setSsoLoginData] = useState(null);
  const [awsKeyData, setAwsKeyData] = useState(null);
  const [awsArnData, setAwsArnData] = useState(null);
  const [gcpData, setGcpData] = useState(null);
  const [azureData, setAzureData] = useState(null);
  const [sftpData, setSftpData] = useState(null);
  let workingIntegrations = []
  let operationalIntegrations = [{ name: "SAML" }, { name: "LDAP" }, { name: "OAUTH2" }, { name: "cloudStorage" }]
  // let operationalIntegrations = [{ name: "ssoLogin" }]

  const getIntegrationDisplayName = (integrationName) => {
    const integrationDisplayNameMap = {
      Idesk: "IDesk",
      ContactManager: "Contact Manager",
      OAUTH2: "OAUTH2",
      cloudStorage: "Cloud Storage",
    };

    return integrationDisplayNameMap[integrationName] || integrationName;
  }

  const recordIntegrationToggleAudit = (integrationName, isEnabled) => {
    try {
      const integrationDisplayName = getIntegrationDisplayName(integrationName);
      const bodyParams = {
        logModule: "Integrations",
        logAction: isEnabled ? "STR" : "UPD",
        logDescription: JSON.stringify({
          message_view: `${integrationDisplayName} Integration ${isEnabled ? "Initiated" : "initiated disable"}`,
        }),
        attributeName: integrationName,
      };

      return liveReportService({ urlParams: "auditLog", bodyParams }).catch((error) => {
        console.error("Error recording integration toggle audit log:", error);
      });
    } catch (error) {
      console.error("Error recording integration toggle audit log:", error);
    }
  }

  // eslint-disable-next-line array-callback-return
  integrationList.map((integration) => {
    if (integration.name === "Zoho" || integration.name === "Idesk" || integration.name === "Netspaeins" || integration.name === "Hubspot" || integration.name === "Balto" || integration.name === "ContactManager") {
      workingIntegrations.push(integration)
    }
  })

  const [tabValue, setTabValue] = useState("1");
  const toggleStyle = useMemo(
    () => ({
      color: "#212121",
      fontSize: " 0.75rem",
      fontWeight: 400,
      letterSpacing: "0em",
      backgroundColor: "#F5F8FA",
      width: "5.8125rem",
      height: "1.875rem",
      borderRadius: 0,
      paddingBottom: "5px",
      paddingTop: "5px",
      '&.Mui-selected, &.Mui-selected:hover': {
        backgroundColor: '#FFFF',
      },
    }),
    []
  );

  const handleTabChange = (event, tabIndex) => {
    setTabValue(tabIndex);
    if (tabIndex === "1") {
      setCrmTab(true)
      setOperationalTab(false)
    }
    if (tabIndex === "2") {
      setOperationalTab(true)
      setCrmTab(false)
    }
  };


  useEffect(() => {
    getIntegrationsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function enableIdesk() {
    setLoading(true);
    try {
      recordIntegrationToggleAudit("Idesk", true);
      enableIdeskIntegration()
        .then((resp) => {
          if (resp.status === "success" && resp.statusCode === 200) {
            showSuccessNotification('Idesk Integration Enabled Successfully');
            getUserIntegrationsData();
            setLoading(false);
          }
          else {
            showErrorNotification("Error in Enabling Idesk Integration");
            setEnableIntegrations(prev => ({ ...prev, Idesk: false }));
            setLoading(false);
          }
        })
        .catch((e) => {
          showErrorNotification("Error in Enabling Idesk Integration");
          setEnableIntegrations(prev => ({ ...prev, Idesk: false }));
          setLoading(false);
        });
    } catch (e) {
      showErrorNotification("Error in Idesk Integration Enabling");
      setEnableIntegrations(prev => ({ ...prev, Idesk: false }));
      setLoading(false);
    }
  };

  async function toggleContactManagerIntegration(mode = "enable") {
    setLoading(true);
    let contactManagerIntegration = mode === "enable" ? false : true;
    try {
      recordIntegrationToggleAudit("ContactManager", mode === "enable");
      const resp = await toggleContactManagerIntegrationData(mode)
      if (resp.status === "success" && resp.statusCode === 200) {
        showSuccessNotification(`Contact Manager Integration ${mode === "enable" ? "Enabled" : "Disabled"} Successfully`);
        getUserIntegrationsData();
        setLoading(false);
      }
      else {
        throw new Error(`Error in Contact Manager Integration ${mode === "enable" ? "Enabling" : "Disabling"}`);
      }
    }
    catch (e) {
      showErrorNotification(`Error in Contact Manager Integration ${mode === "enable" ? "Enabling" : "Disabling"}`);
      setEnableIntegrations(prev => ({ ...prev, ContactManager: contactManagerIntegration }));
      setLoading(false);
    }
  }


  async function removeIdesk() {
    setLoading(true);
    try {
      recordIntegrationToggleAudit("Idesk", false);
      removeIdeskIntegration()
        .then((resp) => {
          if (resp.status === "success" && resp.statusCode === 200) {
            showSuccessNotification('Idesk Integration Revoked Successfully');
            getUserIntegrationsData()
            setLoading(false);
          }
          else {
            showErrorNotification("Error in revoking Idesk Integration");
            setEnableIntegrations(prev => ({ ...prev, Idesk: true }));
            setLoading(false);
          }
        })
        .catch((e) => {
          showErrorNotification("Error in revoking Idesk Integration");
          setEnableIntegrations(prev => ({ ...prev, Idesk: true }));
          setLoading(false);
        });
    } catch (e) {
      showErrorNotification("Error in Idesk Integration revoking");
      setEnableIntegrations(prev => ({ ...prev, Idesk: true }));
      setLoading(false);
    }
  };

  async function revokeIntegrations(integrationName) {
    setLoading(true);
    try {
      recordIntegrationToggleAudit(integrationName, false);
      revokeIntegration(integrationName === "Zoho" ? integrationName.toLowerCase() : integrationName)
        .then((resp) => {
          if (resp.Status === "Success") {
            showSuccessNotification(`${integrationName} Integration Revoked Successfully`);
            getUserIntegrationsData()
            setLoading(false);
          }
          else {
            showErrorNotification(`Error in revoking ${integrationName} Integration`);
            setEnableIntegrations(prev => ({ ...prev, [integrationName]: true }));
            setLoading(false);
          }
        })
        .catch((e) => {
          showErrorNotification(`Error in revoking ${integrationName} Integration`);
          setEnableIntegrations(prev => ({ ...prev, [integrationName]: true }));
          setLoading(false);
        });
    } catch (e) {
      showErrorNotification(`Error in ${integrationName} Integration revoking`);
      setEnableIntegrations(prev => ({ ...prev, [integrationName]: true }));
      setLoading(false);
    }
  };

  async function removeIntegration(integrationName) {
    setLoading(true);
    try {
      recordIntegrationToggleAudit(integrationName, false);
      removeIntegrations(integrationName)
        .then((resp) => {
          if (resp.Status === "success") {
            showSuccessNotification(`${integrationName} Integration Revoked Successfully`);
            getUserIntegrationsData()
            setLoading(false);
          }
          else {
            showErrorNotification(`Error in revoking ${integrationName} Integration`);
            setEnableIntegrations(prev => ({ ...prev, [integrationName]: true }));
            setLoading(false);
          }
        })
        .catch((e) => {
          showErrorNotification(`Error in revoking ${integrationName} Integration`);
          setEnableIntegrations(prev => ({ ...prev, [integrationName]: true }));
          setLoading(false);
        });
    } catch (e) {
      showErrorNotification(`Error in ${integrationName} Integration revoking`);
      setEnableIntegrations(prev => ({ ...prev, [integrationName]: true }));
      setLoading(false);
    }
  }

  const toggleIntegrationState = async (e, integrationName, integrationItem) => {
    if (integrationName !== "ssoLogin" && integrationName !== "cloudStorage") {
      setEnableIntegrations(prev => ({ ...prev, [integrationName]: e.target.checked }));
    }
    if (integrationName === "Idesk") {
      if (e.target.checked === true) {
        enableIdesk()
      }
      if (e.target.checked === false) {
        removeIdesk()
      }
    }
    if (integrationName === "ContactManager") {
      if (e.target.checked === true) {
        toggleContactManagerIntegration("enable")
      }
      if (e.target.checked === false) {
        toggleContactManagerIntegration("disable")
      }
    }
    if (integrationName === "Zoho") {
      if (e.target.checked === true) {
        let state = encodeURIComponent(btoa(`${integrationName}~${window.location.origin}/oauth`))
        let zohoLink = `${integrationItem.codeUrl}?scope=${integrationItem.scope}&client_id=${integrationItem.clientId}&client_secret=${integrationItem.clientSecret}&access_type=offline&response_type=code&state=${state}&redirect_uri=${integrationItem.redirectUrl}`
        if (integrationItem.name === "Zoho") {
          await recordIntegrationToggleAudit(integrationName, true);
          window.open(zohoLink, "_self");
        }
      }
      if (e.target.checked === false) {
        revokeIntegrations(integrationItem.name);
      }
    }
    if (integrationName === "Hubspot") {
      if (e.target.checked === true) {
        let state = encodeURIComponent(btoa(`${integrationName}~${window.location.origin}/oauth`))
        let hubspotLink = `${integrationItem.codeUrl}?scope=${integrationItem.scope}&client_id=${integrationItem.clientId}&client_secret=${integrationItem.clientSecret}&access_type=offline&response_type=code&state=${state}&redirect_uri=${integrationItem.redirectUrl}`
        if (integrationItem.name === "Hubspot") {
          await recordIntegrationToggleAudit(integrationName, true);
          window.open(hubspotLink, "_self");
        }
      }
      if (e.target.checked === false) {
        revokeIntegrations(integrationItem.name);
      }
    }
    if (integrationName === "Netsapiens") {
      if (e.target.checked === true) {
        recordIntegrationToggleAudit(integrationName, true);
        setNetsapiensIntegration(true);
      }
      if (e.target.checked === false) {
        removeIntegration(integrationItem.name)
      }
    }
    if (integrationName === "Balto") {
      if (e.target.checked === true) {
        recordIntegrationToggleAudit(integrationName, true);
        setBaltoIntegration(true);
      }
      if (e.target.checked === false) {
        removeIntegration(integrationItem.name)
      }
    }
    if (SSO_INTEGRATION_TYPES.includes(integrationName)) {
      if (e.target.checked === true) {
        const enabledSsoIntegration = SSO_INTEGRATION_TYPES.find(
          (ssoType) => ssoType !== integrationName && enabledOperationalIntegrations?.[ssoType]
        );
        if (enabledSsoIntegration) {
          showWarningNotification(`Please disable ${enabledSsoIntegration} before enabling ${integrationName}`);
          return;
        }
        removeSsoORCloudStorage("removeSsoDetails", integrationName, true, integrationName)
      }
      if (e.target.checked === false) {
        removeSsoORCloudStorage("removeSsoDetails", integrationName, false, integrationName)
      }
    }
    if (integrationName === "cloudStorage") {
      if (e.target.checked === true) {
        removeSsoORCloudStorage("removeCloudStorageDetails", integrationName, true)
      }
      if (e.target.checked === false) {
        removeSsoORCloudStorage("removeCloudStorageDetails", integrationName, false)
      }
    }
  };

  async function removeSsoORCloudStorage(pathName, integationName, isActive, ssoType) {
    setLoading(true)
    try {
      recordIntegrationToggleAudit(integationName, isActive);
      removeSsoLoginORCloudStorage(pathName, isActive, ssoType)
        .then((resp) => {
          if (resp.Status === "Success") {
            if (isActive === false) {
              showSuccessNotification(`Revoked ${integationName} integration successfully`)
            }
            else {
              showSuccessNotification(`Enabled ${integationName} integration successfully`)
            }
            setEnableOperationalIntegrations(prev => ({ ...prev, [integationName]: isActive }))
            getUserIntegrationsData()
            setLoading(false);
          }
          else {
            if (isActive === false) {
              showErrorNotification(`Error in Revoking  ${integationName} integration`)
            }
            else {
              showErrorNotification(`Error in enabling  ${integationName} integration`)
            }
            setEnableOperationalIntegrations(prev => ({ ...prev, [integationName]: !isActive }));
            setLoading(false);
          }
        })
        .catch((e) => {
          setLoading(false);
          console.error(e);
          showErrorNotification(`Error in revoking ${integationName} Integration`);
          setEnableOperationalIntegrations(prev => ({ ...prev, [integationName]: !isActive }));
        });
    }
    catch {
      setLoading(false);
      showErrorNotification(`Error in Revoking  ${integationName} Integration`)
      setEnableOperationalIntegrations(prev => ({ ...prev, [integationName]: !isActive }));
    }
  }

  function convertToValidJson(inputString) {
    // const trimmedString = inputString.replace(/[{}]/g, "");
    const trimmedString = inputString.replace(/^\{|\}$/g, "");
    const keyValuePairs = trimmedString.split(", ");
    const storageConfig = {};
    keyValuePairs.forEach(pair => {
      // const [key, value] = pair.split("=");
      const [key, value] = pair.split(/=(.+)/);
      storageConfig[key] = isNaN(value) ? value : Number(value);
    });
    console.log(storageConfig);
    return storageConfig
  }

  async function getUserIntegrationsData() {
    let integrations = {};
    let integrationIds = [];
    let integrationData = {}
    try {
      getEnabledUserIntegrations()
        .then((resp) => {
          if (resp.Status === "Success") {
            let ideskData = []
            resp?.Data && resp?.Data?.length > 0 && resp?.Data?.map((item) => {
              if (item.name === "Idesk") {
                ideskData = {
                  ideskIntegration: item.jsonData,
                  token: item.authToken,
                }
                let parsedData = {
                  ideskIntegration: JSON.parse(ideskData.ideskIntegration),
                  token: ideskData.token
                }
                setIdeskData(parsedData)
              }
              let contactManagerData = []
              if (item.name === "ContactManager") {
                
                contactManagerData = {
                  ideskIntegration: item.jsonData,
                  token: item.authToken,
                }
                let parsedData = {
                  ideskIntegration: JSON.parse(contactManagerData.ideskIntegration),
                  token: contactManagerData.token
                }
                setContactManagerData(parsedData)
              }
              if (item.name === "Netsapiens") {
                if (typeof item === "string") {
                  setNetsapiensData(JSON.parse(item))
                } else {
                  setNetsapiensData(item)
                }
              }
              if (item.name === "Balto") {
                setBaltoData(JSON.parse(item))
              }
            })
            resp?.Data && resp?.Data?.length > 0 && resp?.Data?.forEach(element => {
              if (element.name !== "AWS-Key" && element.name !== "AWS-ARN" && element.name !== "GCP" && element.name !== "Azure" && element.name !== "SFTP") {
                integrationIds.push(element.id)
              }
            });
            integrationData = JSON.parse(localStorage.getItem("IntegrationData"));
            integrationData.forEach(element => {
              if (integrationIds.includes(element.id) === true) {
                integrations[element.name] = true;
              }
              else {
                integrations[element.name] = false;
              }
            });
            setEnableIntegrations(integrations);
            setEnableOperationalIntegrations(prev => ({
              ...prev,
              SAML: false,
              LDAP: false,
              OAUTH2: false,
            }));
            setSsoLoginData(null);

            const activeSsoIntegration = resp?.AppUserList?.find(
              (data) => SSO_INTEGRATION_TYPES.includes(data?.authType) && data?.active !== false
            );

            if (activeSsoIntegration) {
              const parsedData = parseSsoAuthConfig(activeSsoIntegration.authConfig);
              let nextSsoLoginData = null;

              if (activeSsoIntegration.authType === "SAML") {
                nextSsoLoginData = {
                  authType: activeSsoIntegration.authType,
                  authConfig: parsedData.url || "",
                  federationUrl: parsedData.federationUrl || "",
                  authFile: [],
                  isAllowAdmin: parsedData.isAllowAdmin || false
                };
              }
              else if (activeSsoIntegration.authType === "LDAP") {
                nextSsoLoginData = {
                  authType: activeSsoIntegration.authType,
                  url: parsedData.url || "",
                  port: parsedData.port || "",
                  userDn: parsedData.userDn || "",
                  domain: parsedData.domain || "",
                  dc: parsedData.dc || "",
                  isAllowAdmin: parsedData.isAllowAdmin || false
                };
              }
              else if (activeSsoIntegration.authType === "OAUTH2") {
                nextSsoLoginData = {
                  authType: activeSsoIntegration.authType,
                  clientId: parsedData.clientId || "",
                  clientSecret: parsedData.clientSecret || "",
                  scope: parsedData.scope || "",
                  authorizationUri: parsedData.authorizationUri || "",
                  tokenUri: parsedData.tokenUri || "",
                  userInfoUri: parsedData.userInfoUri || "",
                  userNameAttributeName: parsedData.userNameAttributeName || "",
                  jwkSetUri: parsedData.jwkSetUri || "",
                };
              }

              setSsoLoginData(nextSsoLoginData);
              setEnableOperationalIntegrations(prev => ({
                ...prev,
                [activeSsoIntegration.authType]: true
              }));
            }
            let awsKeydata = null
            let awsArndata = null
            let gcpdata = null
            let azuredata = null
            resp?.Data && resp?.Data?.length > 0 && resp?.Data?.map((data) => {
              if (data.name !== "Idesk" && data.name !== "Zoho" && data.name !== "ContactManager") {
                // let parsedJson = data?.jsonData !== undefined && data?.jsonData !== null && convertToValidJson(data?.jsonData)
                let parsedJson = data?.jsonData ? JSON.parse(data.jsonData) : data.jsonData;
                if (parsedJson?.name === "AWS-Key") {
                  awsKeydata = {
                    // provider: parsedJson.provider,
                    provider: "AmazonS3",
                    type: parsedJson.type,
                    accessKey: parsedJson.accessKey,
                    secretKey: parsedJson.secretKey,
                    bucket: parsedJson.bucket,
                    folder: parsedJson.folder,
                    isZip: parsedJson.isZip,
                    name: parsedJson.name,
                    date: data.date
                  }
                  setAwsKeyData(awsKeydata)
                  setEnableOperationalIntegrations(prev => ({ ...prev, cloudStorage: true }))
                }
                else if (parsedJson?.name === "AWS-ARN") {
                  const isDualRoleArn = parsedJson.provider === "AmazonS3WithDualRoleArn";
                  awsArndata = {
                    accessKey: parsedJson.accessKey,
                    secretKey: parsedJson.secretKey,
                    // provider: parsedJson.provider,
                    provider: "AmazonS3",
                    type: isDualRoleArn ? "Dual ARN" : parsedJson.type,
                    bucket: parsedJson.bucket,
                    folder: parsedJson.folder,
                    roleArn: parsedJson.roleArn,
                    customerRoleArn: parsedJson.customerRoleArn,
                    ozonetelRoleArn: parsedJson.ozonetelRoleArn,
                    externalId: parsedJson.externalId,
                    isZip: parsedJson.isZip,
                    name: parsedJson.name,
                    date: data.date
                  }
                  setAwsArnData(awsArndata)
                  setEnableOperationalIntegrations(prev => ({ ...prev, cloudStorage: true }))
                }
                else if (parsedJson?.name === "GCP") {
                  const credJson = parsedJson?.credJson ? JSON.parse(parsedJson.credJson) : parsedJson;
                  console.log("credJson", credJson)
                  gcpdata = {
                    // provider: parsedJson.provider,
                    provider: "GoogleCloud",
                    type: parsedJson.type,
                    gcptype: credJson.type,
                    project_id: credJson.project_id,
                    private_key_id: credJson.private_key_id,
                    private_key: credJson.private_key,
                    client_email: credJson.client_email,
                    client_id: credJson.client_id,
                    auth_uri: credJson.auth_uri,
                    token_uri: credJson.token_uri,
                    auth_provider_x509_cert_url: credJson.auth_provider_x509_cert_url,
                    client_x509_cert_url: credJson.client_x509_cert_url,
                    bucket: parsedJson.bucket,
                    folder: parsedJson.folder,
                    isZip: parsedJson.isZip,
                    name: parsedJson.name,
                    date: data.date
                  }
                  setGcpData(gcpdata)
                  setEnableOperationalIntegrations(prev => ({ ...prev, cloudStorage: true }))
                }
                else if (parsedJson?.name === "Azure") {
                  azuredata = {
                    provider: parsedJson.provider,
                    type: parsedJson.type,
                    accountUrl: parsedJson.accountUrl,
                    credential: parsedJson.credential,
                    containerName: parsedJson.containerName,
                    folder: parsedJson.folder,
                    isZip: parsedJson.isZip,
                    name: parsedJson.name,
                    date: data.date
                  }
                  setAzureData(azuredata)
                  setEnableOperationalIntegrations(prev => ({ ...prev, cloudStorage: true }))
                }
                else if (parsedJson?.name === "SFTP") {
                  const sftpdata = {
                    provider: "SFTP",
                    type: null,
                    host: parsedJson.host,
                    port: parsedJson.port,
                    username: parsedJson.username,
                    password: parsedJson.password,
                    remoteFolderPath: parsedJson.remoteFolderPath,
                    isZip: parsedJson.isZip,
                    name: parsedJson.name,
                    date: data.date
                  }
                  setSftpData(sftpdata)
                  setEnableOperationalIntegrations(prev => ({ ...prev, cloudStorage: true }))
                }
              }
              else {
                console.log("No data related to cloudStorage found")
              }
            })
          }
          else {
            console.log(resp.Message);
          }
        })
        .catch((e) => {
          console.error(e);
          showErrorNotification("Error in getEnabled integrations list");
        });
    } catch (e) {
      showErrorNotification("Error in getEnabled integrations list");
    }
  };

  async function getIntegrationsData() {
    try {
      getAvailableIntegration()
        .then((resp) => {
          if (resp.Status === "Success") {
            setIntegrationList(resp.Data);
            localStorage.setItem("IntegrationData", JSON.stringify(resp.Data))
            getUserIntegrationsData();
          }
          else {
            showWarningNotification(resp.Message);
          }
        })
        .catch((e) => {
          setIntegrationList([]);
        });
    } catch (e) {
      showErrorNotification("Error in integrations list");
    }
  };

  const onClickIntegrationCard = (integrationItem) => {
    if (enabledIntegrations[integrationItem.name] === true || enabledOperationalIntegrations[integrationItem.name] === true) {
      if (integrationItem.name === "Zoho") {
        setZohoIntegration(true);
      }
      if (integrationItem.name === "Idesk") {
        if (ideskData.token !== undefined && ideskData.token !== null) {
          setIdeskIntegration(true);
        } else {
          showWarningNotification("Idesk Token is not available")
        }
      }
      if (integrationItem.name === "ContactManager") {
        if (contactManagerData.token !== undefined && contactManagerData.token !== null) {
          setContactManagerIntegration(true);
        } else {
          showWarningNotification("Contact Manager Token is not available")
        }
      }
      if (integrationItem.name === "Netsapiens") {
        setNetsapiensIntegration(true);
      }
      if (integrationItem.name === "Balto") {
        setBaltoIntegration(true);
      }
      if (integrationItem.name === "Hubspot") {
        setHubspotIntegration(true);
      }
      if (integrationItem.name === "SAML") {
        setSsoLogin(true);
        setSelectedSsoType("SAML");
      }
      if (integrationItem.name === "LDAP") {
        setSsoLogin(true);
        setSelectedSsoType("LDAP");
      }
      if (integrationItem.name === "OAUTH2") {
        setSsoLogin(true);
        setSelectedSsoType("OAUTH2");
      }
      if (integrationItem.name === "cloudStorage") {
        setCloudStorage(true);
      }
    }
  };

  const onClickCancelOfSsoLoginDialog = () => {
    setSsoLogin(false);
  }

  const onClickCancelOfCloudStorageDialog = () => {
    setCloudStorage(false);
  }

  function getAccessToShow() {
    if (writeAccess() && (newAuthSubAdminAccess(NewAdminPages.INTEGRATION_SETTINGS, "Edit") || newAuthSubAdminAccess(NewAdminPages.INTEGRATION_SETTINGS, "Full") || newAuthPages(NewAdminPages.INTEGRATION_SETTINGS))) {
      return true;
    } else {
      return false;
    }
  }

  const handleForceRevokeIntegration = async (integrationName) => {
    setLoading(true);
    try {
      recordIntegrationToggleAudit(integrationName, false);
      const resp = await forceRevokeIntegration(integrationName);
      if (resp.Status === "Success") {
        showSuccessNotification(`${integrationName} Integration Revoked Successfully`);
        getUserIntegrationsData();
      } else {
        showErrorNotification(`Error in revoking ${integrationName} Integration`);
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="ozAdminIntegrationCardContainer">
      {loading ?
        (
          <div style={{ position: 'fixed', top: '0', left: '0', width: '100%', height: '100%', backgroundColor: '0,0,0,0.5', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }} >
            <LoadingScreen />
          </div>
        ) :
        !ideskIntegration && !zohoIntegration && !netsapiensIntegration && !baltoIntegration && !hubspotIntegration && !contactManagerIntegration && <div>
          <Grid sx={{ marginBottom: "10px" }}>
            <Typography className="oz-page-header"> Settings / Integrations </Typography>
          </Grid>
          <Stack direction="row" sx={{ marginBottom: "32px" }}>
            <Typography className="oz-page-heading"> Integrations </Typography>
          </Stack>
          <TabContext value={tabValue}>
            <div style={{ display: 'flex', flexGrow: 1, width: "100%" }}>
              <TabList
                TabIndicatorProps={{
                  sx: { backgroundColor: "#008EFF", marginBottom: "1.125rem" },
                }}
                onChange={handleTabChange}
              >
                <OZCustomToggleButton value="1" sx={{ borderRight: "0px", ...toggleStyle }} >
                  CRM
                </OZCustomToggleButton>
                <OZCustomToggleButton value="2" sx={{ ...toggleStyle }}  >
                  Operational
                </OZCustomToggleButton>
              </TabList>
              <span style={{ flexGrow: 1, alignSelf: "flex-end" }}>
                <Divider variant="fullWidth" sx={{ borderColor: "#E6E5E6", marginTop: "-19px" }} />
              </span>
            </div>
          </TabContext>
          {crmTab &&
            <Grid container spacing="20px">
              {workingIntegrations.map((integration, index) => {
                const enableForceRelease = integration?.name === "Zoho";
                return (
                  <Grid item key={index}>
                    <Card className={classes.root}>
                      <Stack direction="row" justifyContent="space-between" alignItems='center' marginTop='24px' marginLeft='24px' marginRight="24px">
                        <Tooltip title={getAccessToShow() && enabledIntegrations[integration.name] ? "Click To Edit" : ""}>
                          <CardMedia onClick={() => getAccessToShow() ? `${onClickIntegrationCard(integration)}` : null}
                            className={(integration.name === "Zoho" && (enabledIntegrations[integration.name] ? classes.zohoImage : classes.zohoImageWithoutCursor)) || ((integration.name === "Idesk" || integration.name === "ContactManager") && (enabledIntegrations[integration.name] ? classes.ozonetelImage : classes.ozonetelImageWithoutCursor)) || (integration.name === "Netsapiens" && (enabledIntegrations[integration.name] ? classes.ozonetelImage : classes.ozonetelImageWithoutCursor)) || (integration.name === "Hubspot" && (enabledIntegrations[integration.name] ? classes.hubspotImage : classes.hubspotImageWithoutCursor)) || (integration.name === "Balto" && (enabledIntegrations[integration.name] ? classes.ozonetelImage : classes.ozonetelImageWithoutCursor))}
                            image={(integration.name === "Zoho" && zoho) || ((integration.name === "Idesk" || integration.name === "ContactManager") && ozonetel) || (integration.name === "Netsapiens" && netsapiens) || (integration.name === "Hubspot" && hubspot) || (integration.name === "Balto" && balto)}
                            alt="card"
                          />
                        </Tooltip>
                        <Stack direction={"row"} gap={.4}>
                          <Tooltip title={(getAccessToShow() && enabledIntegrations[integration.name]) ? "Toogle To Revoke" : (getAccessToShow() && !enabledIntegrations[integration.name]) ? "Toogle To Integrate" : ''}>
                            <div style={{ marginTop: "5px" }}>
                              <SimpleSwitch name={integration.name}
                                disabled={writeAccess() ? newAuthSubAdminAccess(NewAdminPages.INTEGRATION_SETTINGS, "View") : true}
                                checked={enabledIntegrations[integration.name]}
                                onChange={e => { toggleIntegrationState(e, integration.name, integration) }}
                              />
                            </div>
                          </Tooltip>
                          {writeAccess() && enableForceRelease && <Tooltip title={(getAccessToShow() && enabledIntegrations[integration.name]) ? "Force Revoke" : ''}>
                            <IconButton sx={{
                              width: "24px",
                              height: "24px",
                              padding: "0px",
                              margin: "0px",
                              fontSize: "0.5rem",
                              mt: 1.2
                            }} onClick={() => { handleForceRevokeIntegration(integration.name) }} style={{ display: (getAccessToShow() && enabledIntegrations[integration.name]) ? "block" : "none" }}>
                              <ResetIcon />
                            </IconButton>
                          </Tooltip>}
                        </Stack>
                      </Stack>
                      <Stack direction="column">
                        <Tooltip title={getAccessToShow() && enabledIntegrations[integration.name] ? "Click To Edit" : ""}>
                          <CardContent onClick={() => getAccessToShow() ? `${onClickIntegrationCard(integration)}` : null}>
                            <div>
                              <label className={(getAccessToShow() && enabledIntegrations[integration.name]) ? "integrationNameLabel" : (getAccessToShow() && !enabledIntegrations[integration.name]) ? "integrationNameLabelWithoutCursor" : "integrationNameLabelWithoutCursor"} style={{ color: (enabledIntegrations[integration.name]) ? "#3D8BF8" : "#7C878B" }}>
                                {integration.name}
                              </label>
                              <p className={enabledIntegrations[integration.name] ? classes.cardPara : classes.cardParaWithoutCursor}>An Online sales CRM software</p>
                            </div>
                          </CardContent>
                        </Tooltip>
                      </Stack>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          }
          {operationalTab &&
            <Grid container spacing="20px">
              {operationalIntegrations.map((integration, index) => {
                return (
                  <Grid item key={index}>
                    <Card className={classes.root}>
                      <Stack direction="row" justifyContent="space-between" alignItems='center' marginTop='24px' marginLeft='24px' marginRight="24px">
                        <Tooltip title={getAccessToShow() && enabledOperationalIntegrations[integration.name] ? "Click To Edit" : ""}>
                          <CardMedia
                            onClick={() => getAccessToShow() ? `${onClickIntegrationCard(integration)}` : null}
                            className={(SSO_INTEGRATION_TYPES.includes(integration.name) && (enabledOperationalIntegrations[integration.name] ? classes.ssoImage : classes.ssoImageWithoutCursor)) || (integration.name === "cloudStorage" && (enabledOperationalIntegrations[integration.name] ? classes.cloudStorageImage : classes.cloudStorageImageWithoutCursor))}
                            image={(SSO_INTEGRATION_TYPES.includes(integration.name) && ssoImage) || (integration.name === "cloudStorage" && cloudStorageImage)}
                            alt="card"
                          />
                        </Tooltip>
                        <Tooltip title={(getAccessToShow() && enabledOperationalIntegrations[integration.name]) ? "Toogle To Revoke" : (getAccessToShow() && !enabledOperationalIntegrations[integration.name]) ? "Toogle To Integrate" : ""}>
                          {/* <Tooltip title={enabledOperationalIntegrations[integration.name] ? "Toogle To Revoke" : "Toogle To Integrate"}> */}
                          <div style={{ marginTop: "5px" }}>
                            <SimpleSwitch name={integration.name}
                              disabled={writeAccess() ? newAuthSubAdminAccess(NewAdminPages.INTEGRATION_SETTINGS, "View") : true}
                              checked={enabledOperationalIntegrations[integration.name]}
                              onChange={e => { toggleIntegrationState(e, integration.name, integration) }}
                            />
                          </div>
                        </Tooltip>
                      </Stack>
                      <Stack direction="column">
                        <Tooltip title={(getAccessToShow() && enabledOperationalIntegrations[integration.name]) ? "Click To Edit" : ""}>
                          <CardContent
                            onClick={() => getAccessToShow() ? `${onClickIntegrationCard(integration)}` : null}
                          >
                            <div>
                              <label className={(getAccessToShow() && enabledOperationalIntegrations[integration.name]) ? "integrationNameLabel" : (getAccessToShow() && !enabledOperationalIntegrations[integration.name]) ? "integrationNameLabelWithoutCursor" : "integrationNameLabelWithoutCursor"} style={{ color: (enabledOperationalIntegrations[integration.name]) ? "#3D8BF8" : "#7C878B", cursor: '' }}>
                                {/* <label className={enabledOperationalIntegrations[integration.name] ? "integrationNameLabel" : "integrationNameLabelWithoutCursor"} style={{ color: (enabledOperationalIntegrations[integration.name]) ? "#3D8BF8" : "#7C878B" }}> */}
                                {integration.name === "cloudStorage" ? "Cloud Storage" : integration.name}
                              </label>
                            </div>
                          </CardContent>
                        </Tooltip>
                      </Stack>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          }
        </div>
      }
      {ideskIntegration && <IdeskIntegration setIdeskIntegration={setIdeskIntegration} ideskData={ideskData} getUserIntegrationsData={() => { getUserIntegrationsData() }} />}
      {contactManagerIntegration && <ContactManagerIntegration setIdeskIntegration={setContactManagerIntegration} ideskData={contactManagerData} getUserIntegrationsData={() => { getUserIntegrationsData() }} />}
      {zohoIntegration && <Integration setZohoIntegration={setZohoIntegration} zoho={true} hubspot={false} />}
      {hubspotIntegration && <Integration setHubspotIntegration={setHubspotIntegration} zoho={false} hubspot={true} />}
      {netsapiensIntegration && <NetsapiensIntegration setNetsapiensIntegration={setNetsapiensIntegration} netsapiensData={netsapiensData} getUserIntegrationsData={() => { getUserIntegrationsData() }} />}
      {baltoIntegration && <BaltoIntegration setBaltoIntegration={setBaltoIntegration} baltoData={baltoData} getUserIntegrationsData={() => { getUserIntegrationsData() }} />}
      {ssoLogin && <SSOLogin open={ssoLogin} onClickCancel={() => { onClickCancelOfSsoLoginDialog() }} data={ssoLoginData} fixedAuthType={selectedSsoType} getUserIntegrationsData={() => { getUserIntegrationsData() }} />}
      {cloudStorage && <CloudStorage open={cloudStorage} onClickCancel={() => { onClickCancelOfCloudStorageDialog() }} awskey={awsKeyData} awsArn={awsArnData} gcp={gcpData} azure={azureData} sftp={sftpData} getUserIntegrationsData={() => { getUserIntegrationsData() }} />}
    </div>
  );
};
